/* BeMe PDP interactions: FAQ accordion, sticky ATC, final CTA, FBT stack.
   Reimplemented from the design prototypes in theme-native vanilla JS. */

(() => {
  if (window.BemePdpLoaded) return;
  window.BemePdpLoaded = true;

  const money = (cents, currency) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(cents / 100);

  const mainProductForm = () =>
    document.querySelector('.wt-product__add-to-cart_form form[data-type="add-to-cart-form"]');

  /* ---------- Paid-traffic message match ---------- */
  const applyPaidEntryMessage = () => {
    const product = document.querySelector(
      '[data-product-handle="beme-liquid-collagen-peptides-for-ligaments-tendons"]'
    );
    if (!product) return;

    const params = new URLSearchParams(window.location.search);
    const entryKey = params.get('utm_term') || params.get('beme_entry') || '';
    const entries = {
      problem_aware: {
        eyebrow: 'Running + endurance · Keep the week moving',
        subtitle:
          'When one stubborn tendon changes the training plan, muscle recovery is not the whole story. PROTECT supplies 5,000 mg (5 g) TENDOFORTE® daily to support the connective tissue carrying the miles—alongside appropriate progressive exercise.',
      },
      risk_aware: {
        eyebrow: 'The overlooked weak link · More than muscle',
        subtitle:
          'Muscle can feel ready before connective tissue has caught up. PROTECT supplies 5,000 mg (5 g) TENDOFORTE® daily for the tendons and ligaments transferring every rep, stride and change of direction.',
      },
      hybrid_sport: {
        eyebrow: 'Hybrid racing · Run, sled, lunge + repeat',
        subtitle:
          'Eight runs and eight stations demand more than muscle alone. PROTECT supplies 5,000 mg (5 g) TENDOFORTE® daily to support the connective tissue carrying every push, pull and stride.',
      },
      strength_sport: {
        eyebrow: 'Strength training · Every rep travels through tendon',
        subtitle:
          'Protein supports muscle. PROTECT is built for the connective tissue transferring the force, with 5,000 mg (5 g) TENDOFORTE® in one ready-to-drink daily sachet.',
      },
      court_field_sport: {
        eyebrow: 'Court + field · Sprint, stop, land + cut',
        subtitle:
          'Acceleration gets the highlight; deceleration takes the load. PROTECT supplies 5,000 mg (5 g) TENDOFORTE® daily for the tendons and ligaments behind every change of direction.',
      },
    };
    const entry = entries[entryKey];
    if (!entry) return;

    const eyebrow = product.querySelector('[data-beme-entry-eyebrow]');
    const subtitle = product.querySelector('[data-beme-entry-subtitle]');
    if (eyebrow) eyebrow.textContent = entry.eyebrow;
    if (subtitle) subtitle.textContent = entry.subtitle;
    document.documentElement.dataset.bemePaidEntry = entryKey;
  };

  applyPaidEntryMessage();

  /*
   * Keep the visible PDP slide aligned with Shopify's ordered product media.
   *
   * Older BeMe templates rendered bespoke sachet/protocol artwork in the first
   * slide while keeping the real Shopify media URL on the surrounding zoom
   * link. That made slide 01's thumbnail and main image disagree after the new
   * editorial carousels were uploaded. Rebuild only that legacy placeholder
   * from the authoritative media link and matching thumbnail.
   */
  const mediaUrlAtWidth = (href, width) => {
    try {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return href;
    }
  };

  const syncLegacyGalleryHero = () => {
    document.querySelectorAll('gallery-section[data-product-page] [data-gallery]').forEach((gallery) => {
      const legacyHero = gallery.querySelector(
        '[data-swiper-slide] .beme-pdp-hero-sachet, [data-swiper-slide] .beme-mobility-hero-art'
      );
      if (!legacyHero) return;
      if (legacyHero.getAttribute('data-beme-media-sync') === 'pending') return;

      const slide = legacyHero.closest('[data-swiper-slide][data-media-id]');
      const zoomLink = legacyHero.closest('a[href]');
      if (!slide) return;

      const mediaId = String(slide.getAttribute('data-media-id') || '');
      const matchingThumb = Array.from(document.querySelectorAll('[data-slide-media-id]')).find(
        (item) => String(item.getAttribute('data-slide-media-id') || '') === mediaId
      );
      const thumbImage = matchingThumb && matchingThumb.querySelector('img');
      if (!thumbImage) return;

      const sourceUrl =
        (zoomLink && (zoomLink.getAttribute('href') || zoomLink.href)) ||
        thumbImage.currentSrc ||
        thumbImage.src;
      const width = 1100;
      const thumbWidth = parseInt(thumbImage.getAttribute('width'), 10) || width;
      const thumbHeight = parseInt(thumbImage.getAttribute('height'), 10) || width;
      const image = new Image();

      image.className =
        'wt-product__img' +
        (legacyHero.classList.contains('wt-product__img--zoom-cursor') ? ' wt-product__img--zoom-cursor' : '');
      image.alt = thumbImage.getAttribute('alt') || '';
      image.width = width;
      image.height = Math.max(1, Math.round((width * thumbHeight) / thumbWidth));
      image.sizes = '(min-width: 900px) 50vw, 100vw';
      image.srcset = [500, 750, 1000, 1200]
        .map((candidateWidth) => mediaUrlAtWidth(sourceUrl, candidateWidth) + ' ' + candidateWidth + 'w')
        .join(', ');
      image.setAttribute('fetchpriority', 'high');
      image.setAttribute('data-media-id', mediaId);
      legacyHero.setAttribute('data-beme-media-sync', 'pending');

      const replaceLegacyHero = () => {
        if (legacyHero.isConnected) legacyHero.replaceWith(image);
      };
      image.addEventListener('load', replaceLegacyHero, { once: true });
      image.addEventListener(
        'error',
        () => {
          legacyHero.removeAttribute('data-beme-media-sync');
        },
        { once: true }
      );
      image.src = mediaUrlAtWidth(sourceUrl, 1200);
      if (image.complete && image.naturalWidth > 0) replaceLegacyHero();
    });
  };

  const productGallery = document.querySelector('gallery-section[data-product-page]');
  if (productGallery) {
    /*
     * Frontrow injects its sticker into the first media slide after page load.
     * That position obscures the opening editorial image on both desktop and
     * mobile, so remove only the in-gallery instance while leaving the rest of
     * the app and the PDP's review content untouched.
     */
    const removeObstructiveGallerySticker = () => {
      productGallery
        .querySelectorAll('#frontrow-sticker.iframe-frontrow-sticker')
        .forEach((sticker) => sticker.remove());
    };
    const syncProductGallery = () => {
      syncLegacyGalleryHero();
      removeObstructiveGallerySticker();
    };

    syncProductGallery();
    new MutationObserver(syncProductGallery).observe(productGallery, { childList: true, subtree: true });
  }

  const submitMainForm = () => {
    const form = mainProductForm();
    if (!form) return false;
    const btn = form.querySelector('.js-add-to-cart, .product-form__submit');
    if (btn && !btn.disabled) {
      btn.click();
      return true;
    }
    return false;
  };

  /* ---------- FAQ accordion (single-open) ---------- */
  class BemeFaq extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.beme-faq__item'));
      this.items.forEach((item) => {
        const q = item.querySelector('.beme-faq__q');
        if (!q) return;
        q.addEventListener('click', () => {
          const isOpen = item.getAttribute('data-open') === 'true';
          this.items.forEach((i) => {
            i.setAttribute('data-open', 'false');
            const b = i.querySelector('.beme-faq__q');
            if (b) b.setAttribute('aria-expanded', 'false');
          });
          if (!isOpen) {
            item.setAttribute('data-open', 'true');
            q.setAttribute('aria-expanded', 'true');
          }
        });
      });
    }
  }
  customElements.define('beme-faq', BemeFaq);

  /* ---------- Judge.me review preview ---------- */
  class BemeReviewsPreview extends HTMLElement {
    connectedCallback() {
      this.widget = this.querySelector('.beme-reviews__widget');
      this.toggle = this.querySelector('.beme-reviews__toggle');
      this.expanded = false;
      if (!this.widget || !this.toggle) return;

      this.refresh = () => {
        const reviews = Array.from(this.querySelectorAll('#judgeme_product_reviews .jdgm-rev'));
        reviews.forEach((review, index) => {
          review.classList.toggle('beme-review--after-preview', index >= 3);
        });
        this.widget.classList.toggle('is-expanded', this.expanded);
        this.toggle.hidden = reviews.length <= 3;
        this.toggle.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
        this.toggle.textContent = this.expanded ? 'Show fewer reviews' : 'Read all reviews';
      };

      this.toggle.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.refresh();
        if (!this.expanded) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.observer = new MutationObserver(this.refresh);
      this.observer.observe(this.widget, { childList: true, subtree: true });
      this.refresh();
      window.setTimeout(this.refresh, 800);
      window.setTimeout(this.refresh, 2200);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }
  customElements.define('beme-reviews-preview', BemeReviewsPreview);

  /* ---------- Sticky ATC bar ---------- */
  class BemeStickyAtc extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.beme-sticky-atc');
      this.priceEl = this.querySelector('[data-beme-sticky-price]');
      this.packEl = this.querySelector('[data-beme-sticky-pack]');
      this.button = this.querySelector('.beme-sticky-atc__btn');
      this.buttonLabel = this.button ? this.button.textContent.trim() : '';
      this.threshold = parseInt(this.getAttribute('data-threshold'), 10) || 320;

      this.onScroll = () => {
        const stuck = (window.scrollY || document.documentElement.scrollTop) > this.threshold;
        if (this.bar) this.bar.setAttribute('data-stuck', stuck ? 'true' : 'false');
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      if (this.button) this.button.addEventListener('click', () => submitMainForm());

      // Mirror the live buy-box price (purchase-options.js rewrites it on
      // plan/variant changes) into the sticky bar.
      this.priceSource =
        document.querySelector('.wt-product__price') ||
        document.querySelector('.product__price') ||
        document.querySelector('[id^="price-"]');
      if (this.priceSource && this.priceEl) {
        this.syncPrice();
        this.observer = new MutationObserver(() => this.syncPrice());
        this.observer.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
      }

      this.onVariantChange = (event) => {
        if (!event.target.closest('variant-options')) return;
        window.setTimeout(() => this.syncPack(), 0);
        window.setTimeout(() => this.syncPack(), 160);
      };
      document.addEventListener('change', this.onVariantChange);
      this.syncPack();
    }

    syncPrice() {
      const el =
        this.priceSource.querySelector('.price__current-sub') ||
        this.priceSource.querySelector('.price-item--regular') ||
        this.priceSource;
      const match = (el.textContent || '').match(/[^\s]*[\d.,]+/);
      if (match) {
        this.priceEl.textContent = match[0];
        if (this.button && this.buttonLabel) this.button.textContent = this.buttonLabel + ' · ' + match[0];
      }
    }

    syncPack() {
      if (!this.packEl) return;
      const checked = Array.from(document.querySelectorAll('variant-options input[type="radio"]:checked')).find((input) =>
        /sachet|pack|count/i.test(input.name || '')
      );
      if (!checked) return;
      const match = String(checked.value || '').match(/(?:^|\D)(15|30)(?:\D|$)/);
      this.packEl.textContent = match ? match[1] + ' sachets · ' : checked.value + ' · ';
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('change', this.onVariantChange);
      if (this.observer) this.observer.disconnect();
    }
  }
  customElements.define('beme-sticky-atc', BemeStickyAtc);

  /* ---------- Final CTA (adds current buy-box selection) ---------- */
  class BemeFinalCta extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('.beme-final-cta__btn');
      if (btn)
        btn.addEventListener('click', () => {
          if (!submitMainForm()) window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }
  }
  customElements.define('beme-final-cta', BemeFinalCta);

  /* ---------- Frequently bought together ---------- */
  class BemeFbt extends HTMLElement {
    connectedCallback() {
      this.currency = this.getAttribute('data-currency') || 'USD';
      this.discountPct = parseFloat(this.getAttribute('data-discount-pct')) || 0;
      this.basePrice = parseInt(this.getAttribute('data-base-price'), 10) || 0;
      this.baseVariant = this.getAttribute('data-base-variant');
      this.currentPriceEl = this.querySelector('.beme-fbt__card--current .beme-fbt__card-price');
      this.currentPackEl = this.querySelector('[data-beme-fbt-pack]');
      this.variants = [];
      const variantJson = document.querySelector('variant-options script[type="application/json"]');
      if (variantJson) {
        try {
          this.variants = JSON.parse(variantJson.textContent || '[]');
        } catch (e) {
          this.variants = [];
        }
      }

      this.toggles = Array.from(this.querySelectorAll('.beme-fbt__toggle'));
      this.toggles.forEach((btn) => {
        btn.addEventListener('click', () => {
          const pressed = btn.getAttribute('aria-pressed') === 'true';
          btn.setAttribute('aria-pressed', pressed ? 'false' : 'true');
          btn.textContent = pressed ? 'Add +' : 'Added ✓';
          this.recompute();
        });
      });

      this.addBtn = this.querySelector('.beme-fbt__add');
      if (this.addBtn) this.addBtn.addEventListener('click', () => this.addStack());
      this.onVariantChange = (event) => {
        if (!event.target.closest('variant-options')) return;
        window.setTimeout(() => this.syncBaseVariant(), 0);
        window.setTimeout(() => this.syncBaseVariant(), 180);
      };
      document.addEventListener('change', this.onVariantChange);
      this.syncBaseVariant();
      this.recompute();
    }

    syncBaseVariant() {
      const form = mainProductForm();
      const idInput = form && form.querySelector('[name="id"]');
      const id = idInput ? String(idInput.value || '') : '';
      const variant = this.variants.find((item) => String(item.id) === id);
      if (!variant) return;
      this.baseVariant = id;
      this.basePrice = parseInt(variant.price, 10) || this.basePrice;
      if (this.currentPriceEl) this.currentPriceEl.textContent = money(this.basePrice, this.currency);
      if (this.currentPackEl) {
        const packMatch = (variant.options || []).join(' ').match(/(?:^|\D)(15|30)(?:\D|$)/);
        this.currentPackEl.textContent = packMatch ? packMatch[1] + '-sachet pack selected' : '';
      }
      this.recompute();
    }

    selected() {
      return this.toggles
        .filter((b) => b.getAttribute('aria-pressed') === 'true')
        .map((b) => ({
          variant: b.getAttribute('data-variant-id'),
          price: parseInt(b.getAttribute('data-price'), 10) || 0,
        }));
    }

    recompute() {
      const sel = this.selected();
      const count = 1 + sel.length;
      const subtotal = this.basePrice + sel.reduce((a, i) => a + i.price, 0);
      const discount = count >= 2 ? Math.round((subtotal * this.discountPct) / 100) : 0;
      const total = subtotal - discount;

      const countEl = this.querySelector('[data-beme-fbt-count]');
      const saveEl = this.querySelector('[data-beme-fbt-save]');
      const strikeEl = this.querySelector('[data-beme-fbt-strike]');
      const totalEl = this.querySelector('[data-beme-fbt-total]');

      if (countEl)
        countEl.textContent =
          count + (count === 1 ? ' one-time formula' : ' one-time formulas') + ' in your stack';
      if (saveEl)
        saveEl.textContent =
          discount > 0
            ? 'You save ' + money(discount, this.currency) + ' (' + this.discountPct + '% off the stack)'
            : this.discountPct > 0
              ? 'Add one more to save ' + this.discountPct + '%'
              : 'Add another formula to build your stack';
      if (strikeEl) {
        strikeEl.textContent = money(subtotal, this.currency);
        strikeEl.style.display = discount > 0 ? '' : 'none';
      }
      if (totalEl) totalEl.textContent = money(total, this.currency);
      if (this.addBtn && !this.addBtn.disabled)
        this.addBtn.textContent = 'Add one-time stack · ' + money(total, this.currency);
    }

    async addStack() {
      const items = [{ id: parseInt(this.baseVariant, 10), quantity: 1 }].concat(
        this.selected().map((i) => ({ id: parseInt(i.variant, 10), quantity: 1 }))
      );
      this.addBtn.disabled = true;
      const prev = this.addBtn.textContent;
      try {
        const res = await fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error('cart add failed');
        this.addBtn.textContent = 'Added ' + items.length + ' to cart ✓';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'beme-fbt' });
        }
        setTimeout(() => window.location.assign('/cart'), 700);
      } catch (e) {
        this.addBtn.textContent = 'Something went wrong — try again';
        setTimeout(() => {
          this.addBtn.textContent = prev;
        }, 2000);
      } finally {
        setTimeout(() => {
          this.addBtn.disabled = false;
        }, 800);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onVariantChange);
    }
  }
  customElements.define('beme-fbt', BemeFbt);
})();
